import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Lock,
  Bell,
  Shield,
  Camera,
  Save,
  X,
  Check,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { useExamAuth } from '@/hooks/useExamAuth';
import { useAuth } from '@azyntor-labs/nexus-auth';
import { uploadsApi } from '@/api/endpoints';
import { authProfileApi } from '@/api/authClient';
import { toast } from '@/utils/toast';
import { Card, Badge, Button } from '@azyntor-labs/nexus-ui';
import { getErrorMessage } from '@/utils';

export const ProfilePage = () => {
  const { user, roles } = useExamAuth();
  // v4: user lives in the SDK store (read-only from the app). After a
  // profile edit we ask the SDK to refresh so the new fields show up
  // across the app.
  const { refresh } = useAuth();
  const setUser = (_: unknown) => { void refresh(); };
  // Primary role surfaced for display — the v4 User type no longer
  // carries a `role` field (roles live on the JWT).
  const primaryRole = roles[0] ?? '';
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'preferences'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const authWebUrl = import.meta.env.VITE_UMS_WEB_URL || import.meta.env.VITE_NEXUS_AUTH_WEB_URL || 'https://auth.azyntorlabs.com';
  const returnUrl = encodeURIComponent(window.location.href);

  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
  });

  // Fetch fresh profile from nexus-auth on mount
  useEffect(() => {
    authProfileApi.getProfile()
      .then((res) => {
        const data = res.data?.data;
        if (data) {
          setProfileForm({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            phone: data.phone || '',
          });
          setUser({ ...user, first_name: data.first_name, last_name: data.last_name, ...(data as any) });
        }
      })
      .catch(() => {
        // Fallback to auth store data
        setProfileForm({
          first_name: user?.first_name || '',
          last_name: user?.last_name || '',
          phone: '',
        });
      })
      .finally(() => setIsLoadingProfile(false));
  }, []);

  const [preferences, setPreferences] = useState({
    email_notifications: true,
    exam_reminders: true,
    grade_notifications: true,
    violation_alerts: true,
    weekly_summary: false,
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<typeof profileForm>) =>
      authProfileApi.updateProfile(data),
    onSuccess: (response) => {
      const updatedUser = response.data?.data?.user;
      if (updatedUser) {
        setUser({ ...user, ...updatedUser });
      }
      setIsEditing(false);
      toast.success('Profile updated successfully');
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    },
  });

  const handleProfileSave = () => {
    if (!profileForm.first_name || !profileForm.last_name) {
      toast.error('Please fill in first and last name');
      return;
    }
    updateProfileMutation.mutate(profileForm);
  };

  const avatarUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      return uploadsApi.uploadImage(file);
    },
    onSuccess: (response) => {
      const url = response.data?.data?.url;
      if (url && user) {
        authProfileApi.updateProfile({ avatar_url: url }).then((res) => {
          const updatedUser = res.data?.data?.user;
          if (updatedUser) setUser({ ...user, ...updatedUser });
        });
      }
      toast.success('Avatar uploaded successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to upload avatar'));
    },
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      avatarUploadMutation.mutate(file);
    }
  };

  const handlePreferencesSave = () => {
    toast.success('Preferences saved successfully');
  };

  const handleCancelEdit = () => {
    setProfileForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: (user as any)?.phone || '',
    });
    setIsEditing(false);
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">My Profile</h1>
        <p className="text-white/50 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Profile Card */}
      <Card>
        <div className="p-8">
          <div className="flex items-start space-x-6">
            {/* Profile Picture */}
            <div className="relative">
              <div className="w-32 h-32 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center overflow-hidden">
                {(user as any)?.avatar_url ? (
                  <img src={(user as any).avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-16 h-16 text-white" />
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploadMutation.isPending}
                className="absolute bottom-0 right-0 w-10 h-10 bg-[#12121a] rounded-full border-2 border-white/[0.06] flex items-center justify-center hover:bg-white/5 shadow-lg transition-colors"
              >
                <Camera className="w-5 h-5 text-white/70" />
              </button>
            </div>

            {/* User Info */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-1">
                {user?.first_name || ''} {user?.last_name || ''}
              </h2>
              <p className="text-white/60 mb-3">{user?.email}</p>
              <div className="flex items-center space-x-4">
                <Badge variant="teal" size="md">
                  {primaryRole?.replace('_', ' ')}
                </Badge>
                {user?.email_verified && (
                  <span className="flex items-center space-x-1 text-emerald-400 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Verified</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs Card */}
      <Card>
        <div className="border-b border-white/[0.06]">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'profile'
                  ? 'border-teal-500 text-teal-400'
                  : 'border-transparent text-white/50 hover:text-white/70 hover:border-white/30'
              }`}
            >
              <User className="w-4 h-4 inline mr-2" />
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'security'
                  ? 'border-teal-500 text-teal-400'
                  : 'border-transparent text-white/50 hover:text-white/70 hover:border-white/30'
              }`}
            >
              <Lock className="w-4 h-4 inline mr-2" />
              Security
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'preferences'
                  ? 'border-teal-500 text-teal-400'
                  : 'border-transparent text-white/50 hover:text-white/70 hover:border-white/30'
              }`}
            >
              <Bell className="w-4 h-4 inline mr-2" />
              Notifications
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Personal Information</h3>
                {!isEditing ? (
                  <Button variant="outline" onClick={() => setIsEditing(true)}>
                    Edit Profile
                  </Button>
                ) : (
                  <div className="flex space-x-3">
                    <Button variant="secondary" onClick={handleCancelEdit}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleProfileSave}
                      disabled={updateProfileMutation.isPending}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}
              </div>

              {isLoadingProfile ? (
                <div className="text-center py-8 text-white/50">Loading profile...</div>
              ) : (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={profileForm.first_name}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, first_name: e.target.value })
                      }
                      disabled={!isEditing}
                      className="input-dark"
                      placeholder="Enter first name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={profileForm.last_name}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, last_name: e.target.value })
                      }
                      disabled={!isEditing}
                      className="input-dark"
                      placeholder="Enter last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="input-dark"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, phone: e.target.value })
                      }
                      disabled={!isEditing}
                      className="input-dark"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Role
                    </label>
                    <input
                      type="text"
                      value={primaryRole?.replace('_', ' ').toUpperCase() || ''}
                      disabled
                      className="input-dark"
                    />
                    <p className="text-sm text-white/40 mt-1">
                      Contact an administrator to change your role
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">Security Settings</h3>
                <p className="text-sm text-white/50">
                  Manage your password, two-factor authentication, and account security on the central auth portal.
                </p>
              </div>

              <div className="space-y-4 max-w-lg">
                <button
                  onClick={() => { window.location.href = `${authWebUrl}/profile?tab=security&redirect=${returnUrl}`; }}
                  className="w-full flex items-center justify-between p-4 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition-colors group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                      <Lock className="w-5 h-5 text-teal-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">Change Password</p>
                      <p className="text-xs text-white/50">Update your account password</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                </button>

                <button
                  onClick={() => { window.location.href = `${authWebUrl}/profile?tab=security&redirect=${returnUrl}`; }}
                  className="w-full flex items-center justify-between p-4 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] transition-colors group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-teal-500/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-teal-400" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">Two-Factor Authentication</p>
                      <p className="text-xs text-white/50">Add an extra layer of security to your account</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                </button>
              </div>

              {/* Danger Zone */}
              <div className="pt-6 border-t border-white/[0.06]">
                <h4 className="text-sm font-semibold text-red-400 mb-4">Danger Zone</h4>
                <button
                  onClick={() => { window.location.href = `${authWebUrl}/profile?tab=security&redirect=${returnUrl}`; }}
                  className="flex items-center space-x-3 px-4 py-3 rounded-lg border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-colors text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Delete Account</span>
                </button>
                <p className="text-xs text-white/40 mt-2">
                  This action is permanent and cannot be undone.
                </p>
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-2">
                  Notification Preferences
                </h3>
                <p className="text-sm text-white/50">
                  Choose what notifications you want to receive
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start justify-between py-4 border-b border-white/[0.06]">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white mb-1">
                      Email Notifications
                    </h4>
                    <p className="text-sm text-white/50">
                      Receive email notifications about important updates
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setPreferences({
                        ...preferences,
                        email_notifications: !preferences.email_notifications,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.email_notifications ? 'bg-teal-500' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.email_notifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-start justify-between py-4 border-b border-white/[0.06]">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white mb-1">Exam Reminders</h4>
                    <p className="text-sm text-white/50">
                      Get reminded about upcoming exams
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setPreferences({
                        ...preferences,
                        exam_reminders: !preferences.exam_reminders,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.exam_reminders ? 'bg-teal-500' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.exam_reminders ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-start justify-between py-4 border-b border-white/[0.06]">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white mb-1">
                      Grade Notifications
                    </h4>
                    <p className="text-sm text-white/50">
                      Be notified when your exams are graded
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setPreferences({
                        ...preferences,
                        grade_notifications: !preferences.grade_notifications,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.grade_notifications ? 'bg-teal-500' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.grade_notifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {(primaryRole === 'teacher' ||
                  primaryRole === 'admin' ||
                  primaryRole === 'proctor') && (
                  <div className="flex items-start justify-between py-4 border-b border-white/[0.06]">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-white mb-1">
                        Violation Alerts
                      </h4>
                      <p className="text-sm text-white/50">
                        Get alerted about proctoring violations
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setPreferences({
                          ...preferences,
                          violation_alerts: !preferences.violation_alerts,
                        })
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        preferences.violation_alerts ? 'bg-teal-500' : 'bg-white/20'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.violation_alerts ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                )}

                <div className="flex items-start justify-between py-4">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-white mb-1">Weekly Summary</h4>
                    <p className="text-sm text-white/50">
                      Receive a weekly summary of your activity
                    </p>
                  </div>
                  <button
                    onClick={() =>
                      setPreferences({
                        ...preferences,
                        weekly_summary: !preferences.weekly_summary,
                      })
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      preferences.weekly_summary ? 'bg-teal-500' : 'bg-white/20'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        preferences.weekly_summary ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="pt-6">
                <Button onClick={handlePreferencesSave}>
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
